import type { ApiDocsSentimentTypeEnum } from "@tradingApp/api/types/ApiDocs";
import type { InsightsSentimentBgGetResult } from "@tradingApp/components/Insights/types";

/**
 * @example
 * const [typeName, color, bg, image] = insightsSentiment(type);
 */
export function insightsSentiment(
  type?: ApiDocsSentimentTypeEnum,
): [string, string, InsightsSentimentBgGetResult, string] {
  const typeName = insightsSentimentTypeNameGet(type);
  const color = insightsSentimentColorGet(type);
  const bg = insightsSentimentBgGet(type);
  const image = insightsSentimentImageGet(type);

  return [typeName, color, bg, image];
}

export function insightsSentimentTypeNameGet(
  type?: ApiDocsSentimentTypeEnum,
): string {
  switch (true) {
    case type === "STRONG_SHORT":
      return "insights.sentiment.shooort";
    case type === "SHORT":
      return "insights.sentiment.short";
    case type === "NEUTRAL":
      return "insights.sentiment.neutral";
    case type === "LONG":
      return "insights.sentiment.long";
    case type === "STRONG_LONG":
      return "insights.sentiment.looong";
    default:
      return "insights.sentiment.n-a";
  }
}

export function insightsSentimentColorGet(
  type?: ApiDocsSentimentTypeEnum,
): string {
  if (type === "LONG" || type === "STRONG_LONG") {
    return "text-[#65D62B]";
  }

  if (type === "SHORT" || type === "STRONG_SHORT") {
    return "text-[#FF5A00]";
  }

  return "text-zinc-400";
}

export function insightsSentimentBgGet(
  type?: ApiDocsSentimentTypeEnum,
): InsightsSentimentBgGetResult {
  switch (true) {
    case type === "LONG":
      return {
        square:
          "radial-gradient(93.13% 100% at 50.15% 100%, #43D576 0%, #228A59 30%, #00403D 80%)",
        rectangle:
          "radial-gradient(105% 350% at 100% 100%, #43D576 0%, #228A59 30%, #00403D 80%)",
        wide: "bg-[#062D02]",
      };
    case type === "STRONG_LONG":
      return {
        square:
          "radial-gradient(118.82% 100% at 50.15% 100%, #77D601 0%, #3C8E12 30%, #004723 70%)",
        rectangle:
          "radial-gradient(105% 350% at 100% 100%, #77D601 0%, #3C8E12 30%, #004723 70%)",
        wide: "bg-[#062D02]",
      };
    case type === "NEUTRAL":
      return {
        square:
          "radial-gradient(118.14% 100% at 50.15% 100%, #84CFFA 0%, #4284A9 40%, #003A59 80%)",
        rectangle:
          "radial-gradient(105% 350% at 100% 100%, #84CFFA 0%, #4284A9 40%, #003A59 80%)",
        wide: "bg-[#27272A]",
      };
    case type === "SHORT":
      return {
        square:
          "radial-gradient(118.9% 100% at 50.15% 100%, #FB7B6F 0%, #AC3E59 30%, #5C0044 80.34%)",
        rectangle:
          "radial-gradient(105% 350% at 100% 100%, #FB7B6F 0%, #AC3E59 30%, #5C0044 80.34%)",
        wide: "bg-[#4B1D04]",
      };
    case type === "STRONG_SHORT":
      return {
        square:
          "radial-gradient(118.03% 100% at 50.15% 100%, #F26200 0%, #A93100 30%, #740002 70%)",
        rectangle:
          "radial-gradient(105% 350% at 100% 100%, #F26200 0%, #A93100 30%, #740002 70%)",
        wide: "bg-[#4B1D04]",
      };
    default:
      return {
        square:
          "radial-gradient(118.14% 100% at 50.15% 100%, #84CFFA 0%, #4284A9 40%, #003A59 80%)",
        rectangle:
          "radial-gradient(105% 350% at 100% 100%, #A1A1AA 0%, #52525B 30%, #27272A 70%)",
        wide: "bg-[#27272A]",
      };
  }
}

export function insightsSentimentImageGet(
  type?: ApiDocsSentimentTypeEnum,
): string {
  switch (true) {
    case type === "LONG":
      return "/images/insights/long.png";
    case type === "STRONG_LONG":
      return "/images/insights/looong.png";
    case type === "NEUTRAL":
      return "/images/insights/neutral.png";
    case type === "SHORT":
      return "/images/insights/short.png";
    case type === "STRONG_SHORT":
      return "/images/insights/shooort.png";
    default:
      return "/images/insights/no-data.png";
  }
}
